import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject
  , signal, computed
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { timer, Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';

import { NavigationService } from 'src/app/services/core/navigation.service';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { MobileResolutionService } from 'src/app/services/platform/mobile-resolution.service';
import { MapSkin, SettingsService } from 'src/app/services/account/settings.service';
import { LanguageService } from 'src/app/services/account/language.service';
import { WebviewService } from 'src/app/services/platform/webview.service';
import { MapService } from 'src/app/services/map/map.service';
import { StackedModalsService } from 'src/app/services/modal/stacked-modals.service';
import { WaypointService } from 'src/app/services/core/waypoint.service';
import { EvCalculationService } from 'src/app/services/core/ev-calculation.service';
import { ConfirmationDialogComponent } from '../../modals/confirmation-dialog/confirmation-dialog.component';
import { NavigationSetBatteryDialogComponent } from '../../modals/navigation-set-battery-dialog/navigation-set-battery-dialog.component';
import { Computer } from 'src/app/models/computer';
import { RoutePlanElement } from 'src/app/models/route';
import { ChargeState } from 'src/app/models/charge-state';
import { BetterRouteState } from 'src/app/models/better-route';
import { NavigationPlanHeaderComponent } from './navigation-plan-header/navigation-plan-header.component';
import { NavigationPlanNormalStateComponent } from './navigation-plan-normal-state/navigation-plan-normal-state.component';
import { NavigationPlanChargeStateComponent } from './navigation-plan-charge-state/navigation-plan-charge-state.component';
import { NavigationPlanBetterRouteComponent } from './navigation-plan-better-route/navigation-plan-better-route.component';

@Component({
  standalone: true,
  selector: 'app-navigation-plan',
  templateUrl: './navigation-plan.component.html',
  styleUrls: ['./navigation-plan.component.scss'],
  imports: [
    CommonModule,
    NavigationPlanHeaderComponent,
    NavigationPlanNormalStateComponent,
    NavigationPlanChargeStateComponent,
    NavigationPlanBetterRouteComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NavigationPlanComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('npCard', { static: false }) npCard!: ElementRef<HTMLElement>;

  /** How long the better-route offer stays on screen before dismissing itself.
   *  Also drives the countdown strip in the prompt, so the two stay in sync. */
  public readonly betterRouteTimeoutMs = 10000;

  public readonly hasTurn = signal<boolean>(false);
  public readonly speedLimit = signal<number>(0);
  public readonly computersArray = signal<Computer[]>([]);
  public readonly roadLanesVisible = signal<boolean>(false);
  public readonly laneInfoCount = signal<number>(0);
  public readonly floatingInfoVisible = signal<boolean>(true);
  public readonly estimatedBatteryLevel = signal<number>(100);
  public readonly NextWaypoint = signal<boolean>(false);
  public readonly NextWaypointDisplayName = signal<string>('');
  public readonly CannotSkipMoreChargingStation = signal<boolean>(false);
  public readonly mobileVisiblePanel = signal<string>('');
  public readonly arrivedChargerDetails = signal<RoutePlanElement | null>(null);
  public readonly arrivedChargerIsEndpoint = signal<boolean>(false);
  public readonly chargeState = signal<ChargeState>(ChargeState.None);
  public ChargeState = ChargeState;
  public readonly chargeTime = signal<string>('');
  public readonly chargerIconUrl = signal<string>('assets/icons/dark/turn/charge.svg');
  public readonly arrivedAtMessage = signal<string>('');
  public readonly betterRouteMessage = signal<string>('');
  /** A route request is in flight (navigation start, off-route replan,
   *  skipped stop, accepted better route). Shown as a badge on the card. */
  public readonly recalculating = signal<boolean>(false);

  // The charging stop owns the card while it is active, so a better-route offer
  // arriving mid-charge never takes it over.
  public readonly betterRouteVisible = computed<boolean>(
    () => this.betterRouteMessage() != '' && this.chargeState() == ChargeState.None
  );

  public NavigationPlanDialogRef;

  private readonly destroyRef = inject(DestroyRef);
  private navigation: boolean = true;
  private betterRouteTimeoutSub: Subscription | null = null;
  private cardResizeObserver: ResizeObserver | null = null;

  constructor(
    public navigationService: NavigationService,
    private utilsService: UtilsService,
    public mobileResolutionService: MobileResolutionService,
    public settingsService: SettingsService,
    public languageService: LanguageService,
    public webviewService: WebviewService,
    public mapService: MapService,
    private snackBar: MatSnackBar,
    private stackedModalsService: StackedModalsService,
    private waypointService: WaypointService,
    private evCalculationService: EvCalculationService
  ) { }

  ngOnInit(): void {
    this.navigationService.ObservableShowRoadLane.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp != null) {
        this.roadLanesVisible.set(resp);
      }
    });

    this.navigationService.ObservableNextTurn.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp && this.navigation) {
        this.hasTurn.set(!!resp.NextTurnInstruction);
        this.laneInfoCount.set(resp.NextTurnInstruction ? resp.NextTurnInstruction.LaneInfo.length : 0);
        this.estimatedBatteryLevel.set(resp.EstimatedBatteryLevel);

        let nextWpChComputer: Computer | null = null;

        if (resp.DistToNextChargingStation != null && resp.DistToNextWaypoint != null) {
          if (resp.DistToNextWaypoint < resp.DistToNextChargingStation) {
            nextWpChComputer = new Computer('wp', '/assets/icons/' + this.settingsService.getMap().Skin + '/ui/computer_icon_waypoint.svg', resp.DistToNextWaypoint, this.utilsService.timeConverter(resp.TimeToNextWaypoint!), resp.NextWaypointBatteryReservedWarning);
            this.NextWaypointDisplayName.set(resp.NextWaypointDisplayName);
          } else {
            nextWpChComputer = new Computer('cs', '/assets/icons/' + this.settingsService.getMap().Skin + '/ui/chargingplan_icon.svg', resp.DistToNextChargingStation, this.utilsService.timeConverter(resp.TimeToNextChargingStation!), resp.NextChargingStationBatteryReservedWarning);
          }
        } else if (resp.DistToNextWaypoint != null) {
          nextWpChComputer = new Computer('wp', '/assets/icons/' + this.settingsService.getMap().Skin + '/ui/computer_icon_waypoint.svg', resp.DistToNextWaypoint, this.utilsService.timeConverter(resp.TimeToNextWaypoint!), resp.NextWaypointBatteryReservedWarning);
          this.NextWaypointDisplayName.set(resp.NextWaypointDisplayName);
        } else if (resp.DistToNextChargingStation != null) {
          nextWpChComputer = new Computer('cs', '/assets/icons/' + this.settingsService.getMap().Skin + '/ui/chargingplan_icon.svg', resp.DistToNextChargingStation, this.utilsService.timeConverter(resp.TimeToNextChargingStation!), resp.NextChargingStationBatteryReservedWarning);
        }

        const destinationComputer: Computer = new Computer('t', '/assets/icons/' + this.settingsService.getMap().Skin + '/ui/target_icon.svg', resp.DistToDest, this.utilsService.timeConverter(resp.TimeToDest), resp.DestBatteryReservedWarning);

        // Rebuilt on every update instead of being mutated in place. The previous version
        // push/unshift-ed into the existing array and copied values onto the existing Computer
        // objects, so the array reference never changed: the child holding it as an @Input was
        // never told anything happened, and the pills stayed empty until some unrelated event
        // re-rendered it. (That path also dropped the next-waypoint row on the first update,
        // because it overwrote index 0 with the destination while the array still had one entry.)
        this.computersArray.set(nextWpChComputer != null
          ? [nextWpChComputer, destinationComputer]
          : [destinationComputer]);

        this.speedLimit.set(resp.SpeedLimit);
      }
      if (resp && resp.DistToNextWaypoint != null) {
        this.NextWaypointDisplayName.set(resp.NextWaypointDisplayName);
      }
    });

    // This card only exists while navigating, so every route request seen
    // here is a (re)calculation of the active route. Better-route checks run
    // in the background and emit no pending state, so they never show it.
    this.evCalculationService.state$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((state) => {
      this.recalculating.set(state.status == 'pending' && state.kind == 'route');
    });

    this.navigationService.ObservableCannotSkipMoreChargingStation.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp) {
        this.CannotSkipMoreChargingStation.set(resp);
      }
    });

    this.mobileResolutionService.ObservableMobileVisiblePanel.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((panelName) => {
      if (panelName != undefined) {
        this.mobileVisiblePanel.set(panelName);
      }
    });

    this.navigationService.ObservableFloatingInfoVisible.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp != null) {
        this.floatingInfoVisible.set(resp);
      }
    });

    this.navigationService.ObservableArrivedToCharger.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp) {
        const waypoints = this.waypointService.getWaypointsParams();
        if (waypoints[waypoints.length - 1].csid && resp.ChargingStationId == waypoints[waypoints.length - 1].csid) {
          this.arrivedChargerIsEndpoint.set(true);
        } else {
          this.arrivedChargerIsEndpoint.set(false);
        }

        this.arrivedChargerDetails.set(resp);
        this.chargeTime.set(this.utilsService.getChargeTime(
          this.arrivedChargerDetails()!.ChargeTimeHour,
          this.arrivedChargerDetails()!.ChargeTimeMin
        ));

        this.arrivedAtMessage.set(this.languageService.languageJSON.NavigationHud_ArrivedAtCharger
          .replace('$chargername', '<b>' + this.arrivedChargerDetails()?.Name + '</b>'));
      }
    });

    this.navigationService.ObservableChargeStateChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp != undefined && resp != null) {
        this.chargeState.set(resp);
      }
    });

    this.navigationService.ObservableBetterRoute.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp && resp.state == BetterRouteState.ShowPopup) {
        this.betterRouteMessage.set(this.languageService.languageJSON.NavigationHud_ReplanMessage
          .replace('$minuteDifference', String(resp.minutesDifference)));

        // When the countdown strip runs out the offer expires on its own and the
        // card returns to the normal navigation rows.
        this.betterRouteTimeoutSub?.unsubscribe();
        this.betterRouteTimeoutSub = timer(this.betterRouteTimeoutMs)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => this.dismissBetterRoute());
      } else if (!resp) {
        // Cleared by the calculation flow (navigation stopped, route replanned).
        this.clearBetterRoute();
      }
    });

    this.settingsService.ObservableMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((resp) => {
      if (resp) {
        if (resp.Skin == MapSkin.Dark) {
          this.chargerIconUrl.set('assets/icons/dark/ui/charge_accent_outline.svg');
        }
        if (resp.Skin == MapSkin.Light) {
          this.chargerIconUrl.set('assets/icons/dark/turn/charge.svg');
        }
      }
    });
  }

  ngAfterViewInit(): void {
    // Flag navigation as active so the floating map buttons (voice, sliders,
    // follow-mode) can lift themselves above this card, and expose the card's
    // height for that offset.
    document.body.classList.add('nav-plan-active');
    this.publishCardHeight();

    // The card has no fixed height any more: it grows and shrinks with its
    // content (skip button, pager dots, the charging stop, the better-route
    // offer) and with orientation changes. Measuring once would leave the
    // buttons pinned to whatever height the empty card had on the first frame,
    // so keep the published value in sync with the rendered card.
    if (this.npCard) {
      this.cardResizeObserver = new ResizeObserver(() => this.publishCardHeight());
      this.cardResizeObserver.observe(this.npCard.nativeElement);
    }
  }

  private publishCardHeight(): void {
    if (!this.npCard) {
      return;
    }
    const height = this.npCard.nativeElement.offsetHeight;
    document.documentElement.style.setProperty('--nav-card-height', `${height}px`);
  }

  public skipWaypoint(): void {
    let skipmessage = '';
    if (this.NextWaypointDisplayName() != null && this.NextWaypointDisplayName() != undefined) {
      skipmessage = this.languageService.languageJSON.NavigationHud_SkipWaypointWithName_Message
        .replace(`$waypoint`, `<b>${this.NextWaypointDisplayName()}</b>`);
    } else {
      skipmessage = this.languageService.languageJSON.NavigationHud_SkipWaypoint_Message;
    }

    this.NavigationPlanDialogRef = this.stackedModalsService.openModal(ConfirmationDialogComponent, {
      data: {
        title: this.languageService.languageJSON.NavigationHud_SkipWaypoint,
        message: skipmessage,
        buttons: [this.languageService.languageJSON.Global_Skip, this.languageService.languageJSON.Global_Cancel]
      },
      autoFocus: false
    });

    this.NavigationPlanDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.navigationService.SkipNextWaypoint();
      }
      this.NavigationPlanDialogRef = null;
    });
  }

  public skipChargingStation(): void {
    const skipmessage = this.languageService.languageJSON.NavigationHud_SkipChargingStation_Message;

    this.NavigationPlanDialogRef = this.stackedModalsService.openModal(ConfirmationDialogComponent, {
      data: {
        title: this.languageService.languageJSON.NavigationHud_SkipChargingStation,
        message: skipmessage,
        buttons: [this.languageService.languageJSON.Global_Skip, this.languageService.languageJSON.Global_Cancel]
      },
      autoFocus: false
    });

    this.NavigationPlanDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.navigationService.SkipNextChargingStation();
      }
      this.NavigationPlanDialogRef = null;
    });
  }

  public replanNavigation(): void {
    this.navigationService.SkipNextWaypoint();
  }

  /** Accept the shorter route: hands the offer back as a replan request. */
  public acceptBetterRoute(): void {
    const betterRouteValue = this.navigationService.ObservableBetterRoute.value;
    if (betterRouteValue) {
      betterRouteValue.state = BetterRouteState.Replan;
      this.navigationService.ObservableBetterRoute.next(betterRouteValue);
    }
    this.clearBetterRoute();
  }

  /** Declined or expired: the card goes back to the normal navigation rows. */
  public dismissBetterRoute(): void {
    // Drop the offer itself, not just the message. ObservableBetterRoute is a
    // BehaviorSubject, so leaving it at ShowPopup would replay the prompt into
    // a rebuilt card (panel switch, navigation restart).
    if (this.navigationService.ObservableBetterRoute.value) {
      this.navigationService.ObservableBetterRoute.next(null);
    }
    this.clearBetterRoute();
  }

  private clearBetterRoute(): void {
    this.betterRouteTimeoutSub?.unsubscribe();
    this.betterRouteTimeoutSub = null;
    this.betterRouteMessage.set('');
  }

  public startCharge(): void {
    this.navigationService.setChargeState(ChargeState.Charging);
  }

  public finishCharge(): void {
    this.navigationService.setChargeState(ChargeState.ChargeFinish);
    if (this.arrivedChargerDetails()) {
      this.NavigationPlanDialogRef = this.stackedModalsService.openModal(NavigationSetBatteryDialogComponent, {
        data: {
          chargedBatteryLevel: this.arrivedChargerDetails()!.BatteryMaxPercent,
          chargingStationId: this.arrivedChargerDetails()!.ChargingStationId,
          arrivedChargerIsEndpoint: this.arrivedChargerIsEndpoint()
        },
        autoFocus: false
      });
    }

    this.NavigationPlanDialogRef.afterClosed().subscribe(() => {
      if (this.arrivedChargerIsEndpoint()) {
        this.navigationService.ObservableChangeNavigation.next('arrive');
        this.navigationService.setChargeState(ChargeState.None);
      }
      this.NavigationPlanDialogRef = null;
    });
  }

  public stopNavigation(): void {
    this.NavigationPlanDialogRef = this.stackedModalsService.openModal(ConfirmationDialogComponent, {
      data: {
        title: this.languageService.languageJSON.NavigationHud_StopNavigation,
        message: this.languageService.languageJSON.NavigationHud_StopNavigation_Message,
        buttons: [this.languageService.languageJSON.Global_Stop, this.languageService.languageJSON.Global_Cancel]
      },
      autoFocus: false
    });

    this.NavigationPlanDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.navigationService.ObservableChangeNavigation.next('exit');
      }
      this.NavigationPlanDialogRef = null;
    });
  }

  public openBatteryReservedSnackbar(): void {
    this.snackBar.open(this.languageService.languageJSON.WarningSoc_popup, '', {
      duration: 5000,
      panelClass: 'snackbar-warning'
    });
  }

  ngOnDestroy(): void {
    this.betterRouteTimeoutSub?.unsubscribe();
    this.cardResizeObserver?.disconnect();
    this.cardResizeObserver = null;
    if (this.NavigationPlanDialogRef) {
      this.NavigationPlanDialogRef.close();
    }
    document.body.classList.remove('nav-plan-active');
    document.documentElement.style.removeProperty('--nav-card-height');
  }
}
